"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  mockResources
} from "@/lib/mock-data";
import { Activity, Database, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Sidebar } from "@/components/sidebar";
import { Header } from "@/components/header";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { PieLabelRenderProps } from "recharts";
import { getAllResourceCounts, groupResourceCountsByType, getTotalResourceCount, getResourceCountByStatus, getKeyResourceTypeCounts, getResourceTrends, ResourceCount, KeyResourceType, DailyResourceCount } from "@/lib/urm-api";

export default function Dashboard() {
  const [resourceCounts, setResourceCounts] = useState<ResourceCount[]>([]);
  const [totalResourceCount, setTotalResourceCount] = useState<number>(0);
  const [readyResourceCount, setReadyResourceCount] = useState<number>(0);
  const [pendingResourceCount, setPendingResourceCount] = useState<number>(0);
  const [notReadyResourceCount, setNotReadyResourceCount] = useState<number>(0);
  const [keyResourceTypes, setKeyResourceTypes] = useState<KeyResourceType[]>([]);
  const [resourceTrends, setResourceTrends] = useState<DailyResourceCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTotal, setLoadingTotal] = useState(true);
  const [loadingStatusCounts, setLoadingStatusCounts] = useState(true);
  const [loadingKeyTypes, setLoadingKeyTypes] = useState(true);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResourceCounts = async () => {
      try {
        setLoading(true);
        setError(null);
        const counts = await getAllResourceCounts();
        setResourceCounts(counts);
      } catch (err) {
        console.error('Failed to fetch resource counts:', err);
        setError('Failed to fetch resource counts from URM');
      } finally {
        setLoading(false);
      }
    };

    fetchResourceCounts();
  }, []);

  useEffect(() => {
    const fetchTotalCount = async () => {
      try {
        setLoadingTotal(true);
        const count = await getTotalResourceCount();
        setTotalResourceCount(count);
      } catch (err) {
        console.error('Failed to fetch total resource count:', err);
      } finally {
        setLoadingTotal(false);
      }
    };

    fetchTotalCount();
  }, []);

  useEffect(() => {
    const fetchStatusCounts = async () => {
      try {
        setLoadingStatusCounts(true);
        // Fetch counts for each status in parallel
        const [ready, pending, notReady] = await Promise.all([
          getResourceCountByStatus('ready'),
          getResourceCountByStatus('pending'),
          getResourceCountByStatus('not-ready'),
        ]);
        setReadyResourceCount(ready);
        setPendingResourceCount(pending);
        setNotReadyResourceCount(notReady);
      } catch (err) {
        console.error('Failed to fetch status counts:', err);
      } finally {
        setLoadingStatusCounts(false);
      }
    };

    fetchStatusCounts();
  }, []);

  useEffect(() => {
    const fetchKeyResourceTypes = async () => {
      try {
        setLoadingKeyTypes(true);
        const types = await getKeyResourceTypeCounts();
        setKeyResourceTypes(types);
      } catch (err) {
        console.error('Failed to fetch key resource types:', err);
      } finally {
        setLoadingKeyTypes(false);
      }
    };

    fetchKeyResourceTypes();
  }, []);

  useEffect(() => {
    const fetchResourceTrends = async () => {
      try {
        setLoadingTrends(true);
        const trends = await getResourceTrends(6);
        setResourceTrends(trends);
      } catch (err) {
        console.error('Failed to fetch resource trends:', err);
      } finally {
        setLoadingTrends(false);
      }
    };

    fetchResourceTrends();
  }, []);

  // Use VirtualResource count for total, fallback to sum if not available
  const totalResources = totalResourceCount > 0 ? totalResourceCount : resourceCounts.reduce((sum, r) => sum + r.count, 0);
  const resourceTypeDistribution = groupResourceCountsByType(resourceCounts);

  // Use real data from VirtualResource API, fallback to mock data
  const readyResources = readyResourceCount > 0 ? readyResourceCount : mockResources.filter((r) => r.status === "Ready").length;
  const pendingResources = pendingResourceCount > 0 ? pendingResourceCount : mockResources.filter((r) => r.status === "Pending").length;
  const errorResources = notReadyResourceCount > 0 ? notReadyResourceCount : mockResources.filter((r) => r.status === "Error").length;

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto bg-background p-6">
          <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of your URM resources and recent activity
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Resources</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingTotal ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Counting...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{totalResources.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Across all paths (VirtualResource)
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ready</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {loadingStatusCounts ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Counting...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{readyResources.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Operating normally
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Activity className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            {loadingStatusCounts ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Counting...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{pendingResources.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Waiting for reconciliation
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Not Ready</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            {loadingStatusCounts ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Counting...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold">{errorResources.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Need attention
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Key Resource Types</CardTitle>
          <CardDescription>
            Counts for primary resource types
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingKeyTypes ? (
            <div className="flex items-center justify-center h-48">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Loading resource types...</span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {keyResourceTypes.map((resourceType) => (
                <div
                  key={`${resourceType.group}/${resourceType.version}/${resourceType.type}`}
                  className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0"
                >
                  <div className="space-y-1 flex-1">
                    <p className="text-sm font-medium leading-none">
                      {resourceType.displayName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {resourceType.group}/{resourceType.version}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Type: {resourceType.type}
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {resourceType.readyCount !== undefined ? resourceType.readyCount.toLocaleString() : '0'}
                      </p>
                      <p className="text-xs text-muted-foreground">Ready</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">
                        {resourceType.notReadyCount !== undefined ? resourceType.notReadyCount.toLocaleString() : '0'}
                      </p>
                      <p className="text-xs text-muted-foreground">Not Ready</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">
                        {resourceType.count !== undefined ? resourceType.count.toLocaleString() : '0'}
                      </p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Resource Activity Trend</CardTitle>
            <CardDescription>
              {loadingTrends ? 'Loading real-time data from URM...' : 'Resources modified on or after each date (last 7 days)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            {loadingTrends ? (
              <div className="flex flex-col items-center justify-center h-[300px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="mt-4 text-sm text-muted-foreground">Fetching resource trends...</p>
              </div>
            ) : resourceTrends.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[300px]">
                <Database className="h-8 w-8 text-muted-foreground" />
                <p className="mt-4 text-sm text-muted-foreground">No trend data available</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={resourceTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => value.toLocaleString()}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Resources"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resource Type Distribution</CardTitle>
            <CardDescription>
              {loading ? 'Loading real-time data from URM...' : 'Breakdown by resource type'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-[300px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="mt-4 text-sm text-muted-foreground">Fetching resource counts...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-[300px]">
                <AlertCircle className="h-8 w-8 text-red-500" />
                <p className="mt-4 text-sm text-red-500">{error}</p>
                <p className="mt-2 text-xs text-muted-foreground">Make sure the Go backend is running</p>
              </div>
            ) : resourceTypeDistribution.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[300px]">
                <Database className="h-8 w-8 text-muted-foreground" />
                <p className="mt-4 text-sm text-muted-foreground">No resources found</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={resourceTypeDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(props: PieLabelRenderProps) =>
                      `${props.name} ${(((props.value as number) / resourceTypeDistribution.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}%`
                    }
                    outerRadius={100}
                    dataKey="value"
                  >
                    {resourceTypeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => value.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
          </div>
        </main>
      </div>
    </div>
  );
}
